"use client";

import { Check, ChevronLeft, ChevronRight, ChevronsUpDown, FilterX, ListChecks, Loader2, Phone, Search, UsersRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/app/_module/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import { useAgentLeads, AGENT_LEADS_PAGE_SIZE } from "@/app/(authenticated)/lead-assignment/_modules/hooks/queries";
import { US_STATES } from "@/app/(authenticated)/lead-assignment/_modules/components/UnassignedLeads";
import { LeadFullDetails } from "@/app/(authenticated)/leads/_modules/components/LeadFullDetails";
import getStatusColor from "@/app/(authenticated)/leads/_modules/constants/get-status-color";
import { useAllPipelineStages } from "@/app/(authenticated)/leads/_modules/hooks/queries";

/**
 * "Assigned" tab of the Agent Details page — two sections:
 *   1. Assigned Lists: per-list rollup of the agent's assigned leads
 *      (click a list to filter the leads table below to it)
 *   2. Assigned Leads: server-side filtered + paginated table, using the
 *      SAME filter params the Lead Assignment page sends (states, timezone,
 *      disposition, list, name search) via useAgentLeads.
 * Clicking a lead swaps the whole tab for the real /leads/[id] view inline.
 */

const TIMEZONES = ["EST", "CST", "MST", "PST", "AKST", "HST"];

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

interface AssignedListsData {
  lists: {
    list_name: string;
    leads: number;
    appts: number;
    underwriting: number;
    sold: number;
    premium?: string | number;
    last_added?: string;
  }[];
  stages: string[];
  states: string[];
}

function useAssignedLists(agencyId: string, agentId: string) {
  return useQuery<AssignedListsData>({
    queryKey: ["agent-assigned-lists", agencyId, agentId],
    queryFn: async () => {
      const res: any = await handleApiCall(async () =>
        api.get({ url: getApiUrl(`/agencies/${agencyId}/agents/${agentId}/assigned-lists`), auth: true })
      );
      const d = res?.lists ? res : res?.data;
      return {
        lists: d?.lists || [],
        stages: d?.stages || [],
        states: d?.states || [],
      };
    },
    enabled: !!agencyId && !!agentId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000, // keep list rollups live without a manual refresh
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

const ALL = "__all__";

export function AgentAssignedLeads({
  agencyId,
  agentId,
  agentName,
}: {
  agencyId: string;
  agentId: string;
  agentName: string;
}) {
  const [page, setPage] = useState(1);
  /**
   * Which leads this section lists:
   *   "assigned" — leads assigned TO this agent (the original behaviour)
   *   "worked"   — leads they actually worked (call, note, SMS, appointment,
   *                door knock) regardless of assignee.
   * A setter on a shared book has ~0 assigned but hundreds worked, so the
   * toggle is the only way to see their real output.
   */
  const [viewMode, setViewMode] = useState<"assigned" | "worked">("assigned");
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);

  // ── filters (server-side — same params the Lead Assignment page sends)
  const [nameSearch, setNameSearch] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [stageFilter, setStageFilter] = useState<string | undefined>();
  const [stateFilter, setStateFilter] = useState<string | undefined>();
  const [stateOpen, setStateOpen] = useState(false);
  const [timezoneFilter, setTimezoneFilter] = useState<string | undefined>();
  const [listFilter, setListFilter] = useState<string | undefined>();
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(nameSearch.trim()), 400);
    return () => clearTimeout(t);
  }, [nameSearch]);

  const hasFilters = !!(debouncedName || stageFilter || stateFilter || timezoneFilter || listFilter);
  const clearFilters = () => {
    setNameSearch("");
    setDebouncedName("");
    setStageFilter(undefined);
    setStateFilter(undefined);
    setTimezoneFilter(undefined);
    setListFilter(undefined);
    setPage(1);
  };

  const { data: listsData } = useAssignedLists(agencyId, agentId);

  // Same color system the Leads page uses: custom pipeline stages carry a hex
  // color that overrides; system stages fall back to getStatusColor classes.
  const { data: pipelineStages } = useAllPipelineStages();
  const stageHexByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of pipelineStages || []) {
      if (!s?.color) continue;
      m.set(s.name.toLowerCase(), s.color);
      const sysVal = (s as any).systemValue;
      if (sysVal) m.set(String(sysVal).toLowerCase(), s.color);
    }
    return m;
  }, [pipelineStages]);

  // State options: the agent's active states pinned first, then the full US
  // list (same catalog the Lead Assignment page filters on).
  const agentStates = useMemo(() => {
    const have = new Set((listsData?.states || []).map((s) => s.toUpperCase()));
    return {
      active: US_STATES.filter((s) => have.has(s.code)),
      rest: US_STATES.filter((s) => !have.has(s.code)),
    };
  }, [listsData?.states]);

  // Filter options: stages the agent's leads actually have + every pipeline
  // stage defined in the agency (so custom stages are always offered).
  const stageOptions = useMemo(() => {
    const seen = new Map<string, string>(); // lower -> display
    for (const s of listsData?.stages || []) seen.set(s.toLowerCase(), s);
    for (const s of pipelineStages || []) {
      const v = (s as any).systemValue || s.name;
      if (!seen.has(String(v).toLowerCase())) seen.set(String(v).toLowerCase(), String(v));
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [listsData?.stages, pipelineStages]);
  const filters = useMemo(
    () => ({
      leadName: debouncedName || undefined,
      status: stageFilter,
      states: stateFilter,
      timezone: timezoneFilter,
      // "No list" is a display bucket for blank list_name; server can't match it
      listName: listFilter && listFilter !== "No list" ? listFilter : undefined,
    }),
    [debouncedName, stageFilter, stateFilter, timezoneFilter, listFilter]
  );
  const { data, isLoading, isFetching } = useAgentLeads(agentId, page, {
    filters: viewMode === "worked" ? { ...filters, workedByAgentId: agentId } : filters,
  });

  // totalItems only comes back on page 1 — hold onto it across pages
  const [total, setTotal] = useState<number | undefined>();
  const [totalPages, setTotalPages] = useState<number | undefined>();
  useEffect(() => {
    if (data?.totalItems != null) setTotal(data.totalItems);
    if (data?.totalPages != null) setTotalPages(data.totalPages);
  }, [data?.totalItems, data?.totalPages]);
  useEffect(() => {
    // new agent or new filters = new list; restart at page 1 with fresh count
    setPage(1);
    setTotal(undefined);
    setTotalPages(undefined);
  }, [agentId, debouncedName, stageFilter, stateFilter, timezoneFilter, listFilter]);

  const leads = data?.leads || [];

  const openLead = (leadId: string) => {
    // Same nav-context contract the Leads page writes, so LeadFullDetails'
    // prev/next arrows page through this agent's (filtered) list.
    try {
      const leadIds = leads.map((l) => l.id);
      sessionStorage.setItem(
        "leadNavigationContext",
        JSON.stringify({
          leadIds,
          currentPage: page,
          totalPages: totalPages ?? 1,
          totalLeads: total ?? leads.length,
          limit: AGENT_LEADS_PAGE_SIZE,
          queryParams: {},
        })
      );
      sessionStorage.setItem("leadNavigationList", JSON.stringify(leadIds));
    } catch {
      // storage blocked — details still open, just without prev/next
    }
    setOpenLeadId(leadId);
  };

  // Inline detail view replaces the tab content — same component as /leads/[id]
  if (openLeadId) {
    return (
      <LeadFullDetails
        leadId={openLeadId}
        onBack={() => setOpenLeadId(null)}
        onNavigateLead={(id) => setOpenLeadId(id)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Section 1: Assigned Lists ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-primary" />
            Assigned Lists
            {!!listsData?.lists?.length && (
              <span className="text-sm font-normal text-gray-400">· {listsData.lists.length}</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!listsData ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : listsData.lists.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No lists assigned to {agentName} yet</p>
          ) : (
            <div className="max-h-56 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b text-left text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="pb-2 pr-4 font-medium">List</th>
                    <th className="pb-2 pr-4 font-medium text-right">Leads</th>
                    <th className="pb-2 pr-4 font-medium text-right">Appts</th>
                    <th className="pb-2 pr-4 font-medium text-right">Underwriting</th>
                    <th className="pb-2 pr-4 font-medium text-right">Sold</th>
                    <th className="pb-2 pr-4 font-medium text-right">Total Premium Sold</th>
                    <th className="pb-2 font-medium">Last Added</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {listsData.lists.map((l) => {
                    const active = listFilter === l.list_name;
                    return (
                      <tr
                        key={l.list_name}
                        onClick={() => {
                          setListFilter(active ? undefined : l.list_name);
                          setPage(1);
                        }}
                        className={`cursor-pointer transition-colors ${active ? "bg-indigo-50/70" : "hover:bg-gray-50/80"}`}
                        title={active ? "Click to clear this list filter" : "Click to filter leads to this list"}
                      >
                        <td className="py-2 pr-4 font-medium text-gray-900">
                          {l.list_name}
                          {active && <span className="ml-2 text-[10px] font-semibold uppercase text-indigo-500">filtering</span>}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums text-gray-700">{l.leads.toLocaleString()}</td>
                        <td className={`py-2 pr-4 text-right tabular-nums ${Number(l.appts) > 0 ? "font-medium text-cyan-600" : "text-gray-400"}`}>
                          {Number(l.appts || 0).toLocaleString()}
                        </td>
                        <td className={`py-2 pr-4 text-right tabular-nums ${Number(l.underwriting) > 0 ? "font-medium text-amber-500" : "text-gray-400"}`}>
                          {Number(l.underwriting || 0).toLocaleString()}
                        </td>
                        <td className={`py-2 pr-4 text-right tabular-nums ${Number(l.sold) > 0 ? "font-medium text-emerald-600" : "text-gray-400"}`}>
                          {Number(l.sold || 0).toLocaleString()}
                        </td>
                        <td className={`py-2 pr-4 text-right tabular-nums ${Number(l.premium) > 0 ? "font-medium text-emerald-700" : "text-gray-400"}`}>
                          {Number(l.premium) > 0 ? `$${Math.round(Number(l.premium)).toLocaleString()}` : "—"}
                        </td>
                        <td className="py-2 whitespace-nowrap text-gray-500">{fmtDate(l.last_added)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Section 2: Assigned Leads ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UsersRound className="h-4 w-4 text-primary" />
              {viewMode === "worked" ? "Leads Worked On" : "Assigned Leads"}
              {total != null && (
                <span className="text-sm font-normal text-gray-400">· {total.toLocaleString()} total</span>
              )}
            </CardTitle>
            {/* Assigned vs Worked On. Same table, same filters — only the
                server-side scope changes. */}
            <div className="inline-flex rounded-md border bg-muted/40 p-0.5">
              {([
                { key: "assigned", label: "Assigned" },
                { key: "worked", label: "Worked On" },
              ] as const).map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => { setViewMode(v.key); setPage(1); }}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    viewMode === v.key
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-gray-500" onClick={clearFilters}>
                <FilterX className="h-3.5 w-3.5" /> Clear filters
              </Button>
            )}
          </div>

          {/* filter bar — server-side, same params as the Lead Assignment page */}
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <div className="relative col-span-2 sm:col-span-3 lg:col-span-1">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <Input
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                placeholder="Search name…"
                className="h-8 pl-8 text-sm"
              />
            </div>
            <Select value={stageFilter ?? ALL} onValueChange={(v) => setStageFilter(v === ALL ? undefined : v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Pipeline" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All pipelines</SelectItem>
                {stageOptions.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Popover open={stateOpen} onOpenChange={setStateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={stateOpen} className="h-8 w-full justify-between px-3 text-sm font-normal">
                  {stateFilter ? `${US_STATES.find((s) => s.code === stateFilter)?.name || stateFilter} (${stateFilter})` : "State"}
                  <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search states…" />
                  <CommandList className="max-h-64">
                    <CommandEmpty>No state found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all states"
                        onSelect={() => { setStateFilter(undefined); setStateOpen(false); }}
                      >
                        <Check className={`mr-2 h-3.5 w-3.5 ${!stateFilter ? "opacity-100" : "opacity-0"}`} />
                        All states
                      </CommandItem>
                    </CommandGroup>
                    {agentStates.active.length > 0 && (
                      <CommandGroup heading="Has leads in">
                        {agentStates.active.map((s) => (
                          <CommandItem
                            key={s.code}
                            value={`${s.name} ${s.code}`}
                            onSelect={() => { setStateFilter(stateFilter === s.code ? undefined : s.code); setStateOpen(false); }}
                          >
                            <Check className={`mr-2 h-3.5 w-3.5 ${stateFilter === s.code ? "opacity-100" : "opacity-0"}`} />
                            {s.name} ({s.code})
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    <CommandGroup heading="All states">
                      {agentStates.rest.map((s) => (
                        <CommandItem
                          key={s.code}
                          value={`${s.name} ${s.code}`}
                          onSelect={() => { setStateFilter(stateFilter === s.code ? undefined : s.code); setStateOpen(false); }}
                        >
                          <Check className={`mr-2 h-3.5 w-3.5 ${stateFilter === s.code ? "opacity-100" : "opacity-0"}`} />
                          {s.name} ({s.code})
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Select value={timezoneFilter ?? ALL} onValueChange={(v) => setTimezoneFilter(v === ALL ? undefined : v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Timezone" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All timezones</SelectItem>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={listFilter ?? ALL} onValueChange={(v) => { setListFilter(v === ALL ? undefined : v); setPage(1); }}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="List" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All lists</SelectItem>
                {(listsData?.lists || []).map((l) => (
                  <SelectItem key={l.list_name} value={l.list_name}>{l.list_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : leads.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-400">
              {hasFilters ? "No assigned leads match these filters" : `No leads assigned to ${agentName} yet`}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="pb-2 pr-4 font-medium">Lead</th>
                      <th className="pb-2 pr-4 font-medium">Phone</th>
                      <th className="pb-2 pr-4 font-medium">Pipeline</th>
                      <th className="pb-2 pr-4 font-medium">State</th>
                      <th className="pb-2 pr-4 font-medium">List</th>
                      <th className="pb-2 pr-4 font-medium">Last Call</th>
                      <th className="pb-2 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leads.map((lead) => {
                      const stat = lead.leadStat || "New lead";
                      const hex = stageHexByName.get(stat.toLowerCase());
                      return (
                        <tr
                          key={lead.id}
                          onClick={() => openLead(lead.id)}
                          className="cursor-pointer transition-colors hover:bg-gray-50/80"
                        >
                          <td className="py-2.5 pr-4">
                            <p className="font-medium text-gray-900">{lead.name || "Unnamed Lead"}</p>
                            {lead.email && <p className="text-xs text-gray-400">{lead.email}</p>}
                          </td>
                          <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                            {lead.phone ? (
                              <span className="inline-flex items-center gap-1.5">
                                <Phone className="h-3 w-3 text-gray-400" />
                                {formatPhoneNumber(lead.phone)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-2.5 pr-4">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${hex ? "" : getStatusColor(stat)}`}
                              style={hex ? { backgroundColor: hex, color: "#fff", borderColor: hex } : undefined}
                            >
                              {stat}
                            </span>
                          </td>
                          <td className="py-2.5 pr-4 text-gray-600">{lead.state || "—"}</td>
                          <td className="py-2.5 pr-4 max-w-[160px] truncate text-gray-600" title={lead.listName}>
                            {lead.listName || "—"}
                          </td>
                          <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">{fmtDate(lead.lastCall)}</td>
                          <td className="py-2.5 whitespace-nowrap text-gray-600">{fmtDate(lead.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* server-side pagination */}
              <div className="mt-4 flex items-center justify-between border-t pt-3">
                <p className="text-xs text-gray-500">
                  Page {page}
                  {totalPages ? ` of ${totalPages}` : ""}
                  {isFetching && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || isFetching}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" /> Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isFetching || (totalPages ? page >= totalPages : leads.length < AGENT_LEADS_PAGE_SIZE)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
